import React, { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Focus,
  Image as ImageIcon,
  Minus,
  Move,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";
import {
  EVENT_IMAGE_ZOOM_MAX,
  EVENT_IMAGE_ZOOM_MIN,
  clampEventImageZoom,
  stepEventImageZoom,
} from "../../utils/eventImageZoom";
import "./style.css";

const API = process.env.REACT_APP_API_BASE;

/**
 * EventImagePositioner
 * Wraps an event cover <img> and, for admins only, adds a drag-to-reposition
 * overlay. Drag saves immediately to the backend on pointer-up.
 *
 * Props:
 *   eventId     – string   – Firestore campaign doc id
 *   src         – string   – cover image URL
 *   alt         – string
 *   initialX    – number   – saved x% (0-100), default 50
 *   initialY    – number   – saved y% (0-100), default 50
 *   initialZoom – number   – saved zoom (0.7-2.5), default 1
 *   isAdmin     – bool
 *   className   – string   – extra class on the wrapper
 */
const EventImagePositioner = ({
  eventId,
  src,
  alt = "",
  initialX = 50,
  initialY = 50,
  initialZoom = 1,
  isAdmin = false,
  className = "",
}) => {
  const [posX, setPosX] = useState(initialX);
  const [posY, setPosY] = useState(initialY);
  const [zoom, setZoom] = useState(clampEventImageZoom(initialZoom));
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const wrapRef = useRef(null);
  const startRef = useRef(null); // { clientX, clientY, posX, posY }

  // Keep state in sync if parent re-fetches initial values
  useEffect(() => {
    setPosX(initialX);
    setPosY(initialY);
    setZoom(clampEventImageZoom(initialZoom));
  }, [initialX, initialY, initialZoom]);

  const clamp = (v) => Math.min(100, Math.max(0, v));

  const savePosition = useCallback(
    async (x, y, nextZoom = zoom) => {
      if (!eventId) return;
      setSaving(true);
      try {
        const config = process.env.REACT_APP_TOKEN
          ? { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
          : {};
        await axios.put(
          `${API}/event/imagePosition/save?id=${eventId}`,
          {
            x: Math.round(x),
            y: Math.round(y),
            zoom: clampEventImageZoom(nextZoom),
          },
          config
        );
        // toast.success("Poziția imaginii a fost salvată!", { autoClose: 1500 });
      } catch {
        toast.error("Eroare la salvarea poziției");
      } finally {
        setSaving(false);
      }
    },
    [eventId, zoom]
  );

  const updateZoom = useCallback(
    (direction) => {
      const nextZoom = stepEventImageZoom(zoom, direction);
      setZoom(nextZoom);
      savePosition(posX, posY, nextZoom);
    },
    [posX, posY, savePosition, zoom]
  );

  const resetImage = useCallback(() => {
    setPosX(50);
    setPosY(50);
    setZoom(1);
    savePosition(50, 50, 1);
  }, [savePosition]);

  // ── Pointer drag handlers ──────────────────────────────────────────────────
  const onPointerDown = useCallback(
    (e) => {
      if (!isAdmin || !panelOpen || !src) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      startRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        posX,
        posY,
      };
      setDragging(true);
    },
    [isAdmin, panelOpen, src, posX, posY]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragging || !startRef.current || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const dx = e.clientX - startRef.current.clientX;
      const dy = e.clientY - startRef.current.clientY;
      // Each pixel of drag = (100 / dimension)% change
      const dxPct = (dx / rect.width) * 100;
      const dyPct = (dy / rect.height) * 100;
      setPosX(clamp(startRef.current.posX - dxPct));
      setPosY(clamp(startRef.current.posY - dyPct));
    },
    [dragging]
  );

  const onPointerUp = useCallback(
    (e) => {
      if (!dragging) return;
      e.currentTarget.releasePointerCapture(e.pointerId);
      setDragging(false);
      const finalX = clamp(
        startRef.current.posX -
          ((e.clientX - startRef.current.clientX) /
            (wrapRef.current?.getBoundingClientRect().width || 1)) *
            100
      );
      const finalY = clamp(
        startRef.current.posY -
          ((e.clientY - startRef.current.clientY) /
            (wrapRef.current?.getBoundingClientRect().height || 1)) *
            100
      );
      setPosX(finalX);
      setPosY(finalY);
      savePosition(finalX, finalY, zoom);
    },
    [dragging, savePosition, zoom]
  );

  if (!src) {
    return (
      <div className={`eip-placeholder ${className}`}>
        <ImageIcon size={32} strokeWidth={1.2} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className={`eip ${panelOpen && isAdmin ? "eip--edit" : ""} ${dragging ? "eip--dragging" : ""} ${className}`}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <img
        src={src}
        alt={alt}
        draggable="false"
        style={{
          objectPosition: `${posX}% ${posY}%`,
          transform: `scale(${zoom})`,
          transformOrigin: `${posX}% ${posY}%`,
        }}
        onPointerDown={panelOpen && isAdmin ? onPointerDown : undefined}
        className="eip__img"
      />

      {/* Admin overlay */}
      {isAdmin && (
        <>
          {/* Toggle button (top-right) */}
          <button
            type="button"
            className={`eip__fab${panelOpen ? " eip__fab--open" : ""}`}
            onClick={() => setPanelOpen((o) => !o)}
            title={panelOpen ? "Închide poziționare" : "Poziționează imaginea"}
            aria-label={panelOpen ? "Închide poziționarea imaginii" : "Poziționează imaginea"}
          >
            {panelOpen ? (
              <X size={14} strokeWidth={2.4} aria-hidden="true" />
            ) : (
              <Move size={15} strokeWidth={2} aria-hidden="true" />
            )}
          </button>

          {/* Edit mode overlay */}
          {panelOpen && (
            <div className="eip__overlay">
              <div className="eip__hint">
                {saving ? (
                  <span className="eip__saving">Se salvează...</span>
                ) : dragging ? (
                  <span>Trage pentru</span>
                ) : (
                  <span>Trage poza</span>
                )}
              </div>
              {/* Crosshair center indicator */}
              <div
                className="eip__crosshair"
                style={{ left: `${posX}%`, top: `${posY}%` }}
              />
              {/* Arrow nudge buttons */}
              <div className="eip__nudge-grid">
                <button type="button" className="eip__nudge" onClick={() => { const v = clamp(posY - 5); setPosY(v); savePosition(posX, v, zoom); }} title="Sus" aria-label="Mută imaginea în sus">
                  <ArrowUp size={12} strokeWidth={2.5} aria-hidden="true" />
                </button>
                <button type="button" className="eip__nudge" onClick={() => { const v = clamp(posY + 5); setPosY(v); savePosition(posX, v, zoom); }} title="Jos" aria-label="Mută imaginea în jos">
                  <ArrowDown size={12} strokeWidth={2.5} aria-hidden="true" />
                </button>
                <button type="button" className="eip__nudge" onClick={() => { const v = clamp(posX - 5); setPosX(v); savePosition(v, posY, zoom); }} title="Stânga" aria-label="Mută imaginea la stânga">
                  <ArrowLeft size={12} strokeWidth={2.5} aria-hidden="true" />
                </button>
                <button type="button" className="eip__nudge" onClick={() => { const v = clamp(posX + 5); setPosX(v); savePosition(v, posY, zoom); }} title="Dreapta" aria-label="Mută imaginea la dreapta">
                  <ArrowRight size={12} strokeWidth={2.5} aria-hidden="true" />
                </button>
                <button type="button" className="eip__nudge eip__nudge--center" onClick={() => { setPosX(50); setPosY(50); savePosition(50, 50, zoom); }} title="Centru" aria-label="Centrează imaginea">
                  <Focus size={12} strokeWidth={2.4} aria-hidden="true" />
                </button>
              </div>
              <div className="eip__zoom-controls" aria-label="Control zoom imagine">
                <button
                  type="button"
                  className="eip__zoom-button"
                  onClick={() => updateZoom(-1)}
                  disabled={zoom <= EVENT_IMAGE_ZOOM_MIN || saving}
                  aria-label="Micșorează imaginea"
                  title="Zoom out"
                >
                  <Minus size={15} aria-hidden="true" />
                </button>
                <output className="eip__zoom-value" aria-live="polite">
                  {Math.round(zoom * 100)}%
                </output>
                <button
                  type="button"
                  className="eip__zoom-button"
                  onClick={() => updateZoom(1)}
                  disabled={zoom >= EVENT_IMAGE_ZOOM_MAX || saving}
                  aria-label="Mărește imaginea"
                  title="Zoom in"
                >
                  <Plus size={15} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="eip__zoom-button eip__zoom-button--reset"
                  onClick={resetImage}
                  disabled={saving}
                  aria-label="Resetează poziția și zoomul imaginii"
                  title="Resetare"
                >
                  <RotateCcw size={14} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventImagePositioner;
